import { Fragment, type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { auditService, type AuditEvent, type AuditEventFilters } from "../../api/audit.service.js";
import EmptyState from "../../components/EmptyState/index.js";
import LoadingState from "../../components/LoadingState/index.js";
import { PageHeader } from "../../components/PageHeader/index.js";
import styles from "./Audit.module.css";

type FilterForm = {
  actor: string;
  type: string;
  subject: string;
  impersonatedBy: string;
  from: string;
  to: string;
  provenanceState: "" | "captured" | "legacy_missing";
};

const emptyFilters: FilterForm = {
  actor: "",
  type: "",
  subject: "",
  impersonatedBy: "",
  from: "",
  to: "",
  provenanceState: "",
};

function requestFilters(filters: FilterForm): AuditEventFilters {
  const actor = filters.actor.trim();
  return {
    ...(actor.includes("@") ? { actorEmail: actor } : actor ? { actorId: actor } : {}),
    type: filters.type.trim() || undefined,
    subject: filters.subject.trim() || undefined,
    impersonatedBy: filters.impersonatedBy.trim() || undefined,
    from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
    to: filters.to ? new Date(`${filters.to}T23:59:59.999`).toISOString() : undefined,
    provenanceState: filters.provenanceState || undefined,
    limit: 50,
  };
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "medium" }).format(
    new Date(value),
  );
}

function AuditLogPage() {
  const [form, setForm] = useState<FilterForm>(emptyFilters);
  const [filters, setFilters] = useState<AuditEventFilters>({ limit: 50 });
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async (nextFilters: AuditEventFilters, append: boolean) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const result = await auditService.list(nextFilters);
      setEvents(current => (append ? [...current, ...result.events] : result.events));
      setNextCursor(result.nextCursor);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error("Failed to load audit events"));
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filters, false);
  }, [filters, load]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setExpandedId(null);
    setFilters(requestFilters(form));
  }

  function clear() {
    setForm(emptyFilters);
    setExpandedId(null);
    setFilters({ limit: 50 });
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Audit" subtitle="Durable event history and actor provenance" />
      <form className={styles.filters} onSubmit={submit}>
        <FilterField label="Actor ID or email">
          <input
            className={styles.input}
            value={form.actor}
            onChange={event => setForm({ ...form, actor: event.target.value })}
          />
        </FilterField>
        <FilterField label="Event type or prefix:*">
          <input
            className={styles.input}
            value={form.type}
            onChange={event => setForm({ ...form, type: event.target.value })}
            placeholder="staff:impersonation:*"
          />
        </FilterField>
        <FilterField label="Subject">
          <input
            className={styles.input}
            value={form.subject}
            onChange={event => setForm({ ...form, subject: event.target.value })}
          />
        </FilterField>
        <FilterField label="Impersonated by">
          <input
            className={styles.input}
            value={form.impersonatedBy}
            onChange={event => setForm({ ...form, impersonatedBy: event.target.value })}
          />
        </FilterField>
        <FilterField label="From">
          <input
            className={styles.input}
            type="date"
            value={form.from}
            onChange={event => setForm({ ...form, from: event.target.value })}
          />
        </FilterField>
        <FilterField label="To">
          <input
            className={styles.input}
            type="date"
            value={form.to}
            onChange={event => setForm({ ...form, to: event.target.value })}
          />
        </FilterField>
        <FilterField label="Provenance">
          <select
            className={styles.select}
            value={form.provenanceState}
            onChange={event =>
              setForm({
                ...form,
                provenanceState: event.target.value as FilterForm["provenanceState"],
              })
            }
          >
            <option value="">All</option>
            <option value="captured">Captured</option>
            <option value="legacy_missing">Unavailable</option>
          </select>
        </FilterField>
        <div className={styles.actions}>
          <button className={`${styles.button} ${styles.buttonPrimary}`} type="submit">
            Apply filters
          </button>
          <button className={styles.button} type="button" onClick={clear}>
            Clear
          </button>
        </div>
      </form>

      {loading && <LoadingState message="Loading audit events..." />}
      {!loading && error && (
        <EmptyState
          variant="error"
          title="Failed to load audit events"
          message={error.message}
          action={{ label: "Retry", onClick: () => void load(filters, false) }}
        />
      )}
      {!loading && !error && events.length === 0 && (
        <EmptyState
          variant="empty"
          title="No audit events"
          message="No events match the selected filters."
        />
      )}
      {!loading && !error && events.length > 0 && (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Subject</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <Fragment key={event.id}>
                    <tr
                      className={styles.eventRow}
                      onClick={() =>
                        setExpandedId(current => (current === event.id ? null : event.id))
                      }
                      aria-expanded={expandedId === event.id}
                    >
                      <td>{formatDate(event.occurredAt)}</td>
                      <td>
                        {event.actor ? (
                          <div>
                            <div>{event.actor.name || event.actor.email || event.actor.id}</div>
                            <div className={styles.secondary}>
                              {event.actor.type} · {event.actor.id}
                            </div>
                          </div>
                        ) : (
                          <span className={styles.unavailable}>Provenance unavailable</span>
                        )}
                      </td>
                      <td className={styles.mono}>{event.type}</td>
                      <td className={styles.mono}>{event.subject || "-"}</td>
                      <td>{event.source}</td>
                    </tr>
                    {expandedId === event.id && (
                      <tr>
                        <td className={styles.detailsCell} colSpan={5}>
                          <div className={styles.detailsGrid}>
                            <JsonBlock title="Payload" value={event.payload} />
                            <JsonBlock title="Metadata" value={event.metadata} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {nextCursor && (
            <div className={styles.loadMore}>
              <button
                className={styles.button}
                type="button"
                disabled={loadingMore}
                onClick={() => void load({ ...filters, cursor: nextCursor }, true)}
              >
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      {children}
    </label>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <div>
      <strong>{title}</strong>
      <pre className={styles.json}>{JSON.stringify(value, null, 2)}</pre>
    </div>
  );
}

export default AuditLogPage;
